import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { queryKeys } from 'core/api/queryKeys';
import { setExpectedRevision } from 'core/api/revisionStore';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError, mapFieldErrors } from 'core/errors/mapFieldErrors';
import { Button } from 'shared/components/Button';
import { Badge, EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { useConfirm } from 'shared/components/ConfirmDialog';
import { useToast } from 'shared/components/Toast';
import { BilingualField, SwitchField } from 'shared/forms/Fields';
import { QuestionnaireWorkbookCard } from 'features/workbooks/QuestionnaireWorkbookCard';
import { dndAnnouncements } from 'shared/a11y/dndAnnouncements';
import { applyBoardDragEnd, localizedName, questionHasPendingWeights } from 'shared/utilities/format';

function SortableItem({ id, children, disabled, label }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      className={isDragging ? 'sortable-item is-dragging' : 'sortable-item'}
    >
      <button type="button" className="drag-handle" aria-label={label} disabled={disabled} {...attributes} {...listeners}>
        ⋮⋮
      </button>
      {children}
    </div>
  );
}

const emptySectionForm = {
  title_ar: '',
  title_en: '',
  description_ar: '',
  description_en: '',
  is_active: true,
};

export function BuilderPage() {
  const { t, locale } = useI18n();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const canSections = hasPermission(PERMISSIONS.sectionsManage);
  const canQuestions = hasPermission(PERMISSIONS.questionsManage);
  const [sectionForm, setSectionForm] = useState(emptySectionForm);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionErrors, setSectionErrors] = useState({});

  const sectionsQuery = useQuery({ queryKey: queryKeys.sections, queryFn: ({ signal }) => assessmentApi.listSections({ signal }) });
  const questionsQuery = useQuery({ queryKey: queryKeys.questions(), queryFn: ({ signal }) => assessmentApi.listQuestions(null, { signal }) });

  const board = useMemo(() => {
    const sections = [...(sectionsQuery.data?.items || [])].sort((a, b) => a.sort_order - b.sort_order);
    const questions = questionsQuery.data?.items || [];
    return sections.map((section) => ({
      ...section,
      questions: questions
        .filter((question) => question.assessment_section_id === section.id)
        .sort((a, b) => a.sort_order - b.sort_order),
    }));
  }, [questionsQuery.data, sectionsQuery.data]);

  const [optimistic, setOptimistic] = useState(null);
  const visible = optimistic || board;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleStale = async (error) => {
    if (error instanceof ApiError && error.isStaleRevision()) {
      if (error.errors?.current_revision) {
        setExpectedRevision(error.errors.current_revision);
      }
      await Promise.all([sectionsQuery.refetch(), questionsQuery.refetch()]);
      toast.error(t('errors.staleRevision'));
      return true;
    }
    return false;
  };

  const createSection = useMutation({
    mutationFn: () => assessmentApi.createSection(sectionForm),
    onSuccess: async () => {
      setShowSectionForm(false);
      setSectionForm(emptySectionForm);
      await queryClient.invalidateQueries({ queryKey: queryKeys.sections });
      toast.success(t('status.created'));
    },
    onError: (error) => {
      if (error instanceof ApiError && error.isValidation()) {
        setSectionErrors(mapFieldErrors(error.errors));
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const updateSection = useMutation({
    mutationFn: ({ id, body }) => assessmentApi.updateSection(id, body),
    onSuccess: async () => {
      setEditingSection(null);
      setSectionErrors({});
      await queryClient.invalidateQueries({ queryKey: queryKeys.sections });
      toast.success(t('status.updated'));
    },
    onError: async (error) => {
      if (await handleStale(error)) {
        return;
      }
      if (error instanceof ApiError && error.isValidation()) {
        setSectionErrors(mapFieldErrors(error.errors));
      }
      toast.error(humanizeApiError(error, t));
    },
  });

  const deleteSection = useMutation({
    mutationFn: (id) => assessmentApi.deleteSection(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.sections });
      toast.success(t('status.deleted'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const reorderSections = useMutation({
    mutationFn: (ids) => assessmentApi.reorderSections(ids),
    onError: async (error) => {
      setOptimistic(null);
      if (!(await handleStale(error))) {
        toast.error(humanizeApiError(error, t));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.sections });
      setOptimistic(null);
    },
  });

  const reorderQuestions = useMutation({
    mutationFn: (questions) => assessmentApi.reorderQuestions(questions),
    onError: async (error) => {
      setOptimistic(null);
      if (!(await handleStale(error))) {
        toast.error(humanizeApiError(error, t));
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.questions() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sections }),
      ]);
      setOptimistic(null);
    },
  });

  const toggleQuestion = useMutation({
    mutationFn: ({ id, isActive }) => assessmentApi.setQuestionActivation(id, isActive),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.questions() }),
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  const deleteQuestion = useMutation({
    mutationFn: (id) => assessmentApi.deleteQuestion(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.questions() });
      toast.success(t('status.deleted'));
    },
    onError: (error) => toast.error(humanizeApiError(error, t)),
  });

  if (sectionsQuery.isLoading || questionsQuery.isLoading) return <Skeleton rows={8} />;
  if (sectionsQuery.isError) return <ErrorState title={humanizeApiError(sectionsQuery.error, t)} onRetry={sectionsQuery.refetch} retryLabel={t('actions.retry')} />;
  if (questionsQuery.isError) return <ErrorState title={humanizeApiError(questionsQuery.error, t)} onRetry={questionsQuery.refetch} retryLabel={t('actions.retry')} />;

  const draggingLocked = reorderSections.isPending || reorderQuestions.isPending;

  const onDragEnd = (event) => {
    const result = applyBoardDragEnd(
      visible,
      event.active ? String(event.active.id) : '',
      event.over ? String(event.over.id) : '',
      draggingLocked,
    );
    if (!result) {
      return;
    }
    setOptimistic(result.next);
    if (result.type === 'sections') {
      reorderSections.mutate(result.sectionIds);
      return;
    }
    reorderQuestions.mutate(result.questions);
  };

  return (
    <div>
      <PageHeader
        title={t('builder.title')}
        description={t('builder.dragHint')}
        actions={canSections ? (
          <Button onClick={() => setShowSectionForm((value) => !value)}>{t('builder.newSection')}</Button>
        ) : null}
      />
      <QuestionnaireWorkbookCard />
      {showSectionForm ? (
        <form
          className="card stack-form"
          onSubmit={(event) => {
            event.preventDefault();
            createSection.mutate();
          }}
        >
          <BilingualField
            id="section-title"
            labelAr={t('sections.titleAr')}
            labelEn={t('sections.titleEn')}
            valueAr={sectionForm.title_ar}
            valueEn={sectionForm.title_en}
            onChangeAr={(value) => setSectionForm((current) => ({ ...current, title_ar: value }))}
            onChangeEn={(value) => setSectionForm((current) => ({ ...current, title_en: value }))}
            errorAr={sectionErrors.title_ar?.[0]}
            errorEn={sectionErrors.title_en?.[0]}
            required
          />
          <BilingualField
            id="section-description"
            labelAr={t('sections.descriptionAr')}
            labelEn={t('sections.descriptionEn')}
            valueAr={sectionForm.description_ar}
            valueEn={sectionForm.description_en}
            onChangeAr={(value) => setSectionForm((current) => ({ ...current, description_ar: value }))}
            onChangeEn={(value) => setSectionForm((current) => ({ ...current, description_en: value }))}
            required={false}
          />
          <SwitchField
            id="section-active"
            label={t('patterns.active')}
            checked={sectionForm.is_active}
            onChange={(value) => setSectionForm((current) => ({ ...current, is_active: value }))}
          />
          <Button type="submit" loading={createSection.isPending} disabled={createSection.isPending}>{t('actions.create')}</Button>
        </form>
      ) : null}
      {visible.length === 0 ? <EmptyState title={t('empty.sections')} /> : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd} accessibility={{ announcements: dndAnnouncements(t) }}>
          <SortableContext items={visible.map((section) => `section-${section.id}`)} strategy={verticalListSortingStrategy}>
            {visible.map((section) => (
              <SortableItem key={section.id} id={`section-${section.id}`} disabled={draggingLocked} label={t('builder.dragHandle')}>
                <article className="section-card">
                  <header>
                    <h2>{localizedName(section, locale)}</h2>
                    <Badge>{section.questions.length} {t('builder.questionCount')}</Badge>
                    {canQuestions ? <Link className="btn btn-secondary" to={`${paths.questionCreate}?section_id=${section.id}`}>{t('builder.newQuestion')}</Link> : null}
                    {canSections ? (
                      <Button
                        tone="secondary"
                        onClick={() => {
                          setEditingSection(section.id);
                          setSectionErrors({});
                          setSectionForm({
                            title_ar: section.title_ar || '',
                            title_en: section.title_en || '',
                            description_ar: section.description_ar || '',
                            description_en: section.description_en || '',
                            is_active: section.is_active !== false,
                          });
                        }}
                      >
                        {t('sections.edit')}
                      </Button>
                    ) : null}
                    {canSections ? (
                      <Button
                        tone="danger"
                        onClick={async () => {
                          if (await confirm({ body: t('confirm.delete') })) deleteSection.mutate(section.id);
                        }}
                      >
                        {t('actions.delete')}
                      </Button>
                    ) : null}
                  </header>
                  {editingSection === section.id ? (
                    <form
                      className="card stack-form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        updateSection.mutate({ id: section.id, body: sectionForm });
                      }}
                    >
                      <BilingualField
                        id={`edit-section-title-${section.id}`}
                        labelAr={t('sections.titleAr')}
                        labelEn={t('sections.titleEn')}
                        valueAr={sectionForm.title_ar}
                        valueEn={sectionForm.title_en}
                        onChangeAr={(value) => setSectionForm((current) => ({ ...current, title_ar: value }))}
                        onChangeEn={(value) => setSectionForm((current) => ({ ...current, title_en: value }))}
                        errorAr={sectionErrors.title_ar?.[0]}
                        errorEn={sectionErrors.title_en?.[0]}
                        required
                      />
                      <BilingualField
                        id={`edit-section-description-${section.id}`}
                        labelAr={t('sections.descriptionAr')}
                        labelEn={t('sections.descriptionEn')}
                        valueAr={sectionForm.description_ar}
                        valueEn={sectionForm.description_en}
                        onChangeAr={(value) => setSectionForm((current) => ({ ...current, description_ar: value }))}
                        onChangeEn={(value) => setSectionForm((current) => ({ ...current, description_en: value }))}
                        required={false}
                      />
                      <SwitchField
                        id={`edit-section-active-${section.id}`}
                        label={t('patterns.active')}
                        checked={sectionForm.is_active}
                        onChange={(value) => setSectionForm((current) => ({ ...current, is_active: value }))}
                      />
                      {updateSection.isError && !updateSection.error?.isValidation?.() ? (
                        <p className="field-error">{humanizeApiError(updateSection.error, t)}</p>
                      ) : null}
                      <div className="toolbar">
                        <Button type="submit" loading={updateSection.isPending} disabled={updateSection.isPending}>{t('actions.save')}</Button>
                        <Button tone="ghost" onClick={() => setEditingSection(null)}>{t('actions.cancel')}</Button>
                      </div>
                    </form>
                  ) : null}
                  <SortableContext items={section.questions.map((question) => `question-${question.id}`)} strategy={verticalListSortingStrategy}>
                    {section.questions.length === 0 ? <EmptyState title={t('empty.questions')} /> : section.questions.map((question) => (
                      <SortableItem key={question.id} id={`question-${question.id}`} disabled={draggingLocked} label={t('builder.dragHandle')}>
                        <div className="question-card">
                          <p>{localizedName(question, locale)}</p>
                          <p className="muted">{(question.answers || []).length} {t('builder.answerCount')}</p>
                          {questionHasPendingWeights(question) ? <Badge tone="warning">{t('patterns.zeroWarning')}</Badge> : null}
                          {canQuestions ? (
                            <>
                              <Link to={paths.questionEdit(question.id)}>{t('actions.edit')}</Link>
                              <Button tone="ghost" disabled={toggleQuestion.isPending} onClick={() => toggleQuestion.mutate({ id: question.id, isActive: !question.is_active })}>
                                {question.is_active ? t('actions.deactivate') : t('actions.activate')}
                              </Button>
                              <Button
                                tone="danger"
                                onClick={async () => {
                                  if (await confirm({ body: t('confirm.delete') })) deleteQuestion.mutate(question.id);
                                }}
                              >
                                {t('actions.delete')}
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </SortableItem>
                    ))}
                  </SortableContext>
                </article>
              </SortableItem>
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
